import {useRefObjectAsForwardedRef} from '@primer/react'
import type React from 'react'
import {createContext, useCallback, useContext, useId, useMemo, useRef, useState} from 'react'
import {useLayoutEffect} from '@github-ui/use-layout-effect'
import {DefaultParser} from '../parser'
import {FilterQueryRequest} from '../filter-request'
import {
  type Parser,
  type FilterSuggestion,
  type FilterProvider,
  BlockType,
  FilterValueType,
  FILTER_PRIORITY_DISPLAY_THRESHOLD,
  type ARIAFilterSuggestion,
  ProviderSupportStatus,
  type FilterSettings,
  defaultFilterSettings,
  type FilterContext as FilterContextType,
  type FilterConfig,
  type FilterVariant,
} from '../types'
import {hasMatch} from 'fzy.js'
import {
  checkFilterQuerySync,
  getEscapedFilterValue,
  getFilterBlockChunkByCaret,
  getFilterValuesByStateType,
  getUnescapedFilterValue,
  isFilterBlock,
  isIndexedFilterBlock,
} from '../utils'
import {Strings} from '../constants/strings'
import {SuggestionsContext} from './SuggestionsContext'
import {FilterQueryContext} from './FilterQueryContext'
import {InputContext} from './InputContext'
import {announce} from '@github-ui/aria-live'
import {useDelimiterAnchorPosition} from '../hooks/use-delimiter-anchor-position'
import type {IsFilterProvider, StateFilterProvider} from '../providers'
import {NoFilterProvider} from '../providers/no'
import {FILTER_VALUES} from '../constants/filter-constants'
import {useDebounce} from '@github-ui/use-debounce'

// This is a delay we configure to avoid screen readers from reading the suggestions list immediatly on change
const SCREEN_READER_FEEDBACK_TIMEOUT_MS = 150

export const FilterContext = createContext<FilterContextType | undefined>(undefined)

export const useFilter = () => {
  const context = useContext(FilterContext)
  if (!context) {
    throw new Error('useFilter must be used inside a FilterProvider')
  }

  return context
}

export type FilterProviderProps = {
  children: React.ReactNode
  customParser?: Parser<FilterQueryRequest>
  rawFilter: string
  filterDelimiter?: string
  inputRef?: React.RefObject<HTMLInputElement> | null
  isFetchingResults?: boolean
  onChange: (value: string) => void
  onParse?: (request: FilterQueryRequest) => void
  onSubmit?: (request: FilterQueryRequest) => void
  onValidation: (messages: string[]) => void
  providers: FilterProvider[]
  settings?: FilterSettings
  valueDelimiter?: string
  variant: FilterVariant
}

export const FilterContextProvider = ({
  children,
  customParser,
  rawFilter,
  filterDelimiter = ':',
  inputRef: forwardedInputRef = null,
  isFetchingResults = false,
  onChange,
  onParse,
  onSubmit,
  onValidation,
  providers: externalProviders,
  settings: suppliedSettings,
  valueDelimiter = ',',
  variant,
}: FilterProviderProps) => {
  const filterConfig = useMemo((): FilterConfig => {
    return {
      filterDelimiter,
      valueDelimiter,
      ...defaultFilterSettings,
      ...suppliedSettings,
      variant,
    }
  }, [filterDelimiter, suppliedSettings, valueDelimiter, variant])

  // Element Refs
  const cursorRef = useRef<HTMLSpanElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  useRefObjectAsForwardedRef(forwardedInputRef, inputRef)
  const suggestionsListRef = useRef<HTMLDivElement>(null)
  const parser = useRef(customParser ?? new DefaultParser(inputRef, filterConfig))
  const screenReaderTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const fetchingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const latestRawFilterRef = useRef('')

  // State
  const [isInteractingWithSuggestions, setIsInteractingWithSuggestions] = useState(false)
  const [filterQuery, setFilterQuery] = useState<FilterQueryRequest>(new FilterQueryRequest('', []))
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false)
  const [suggestionsVisible, setSuggestionsVisible] = useState(false)
  const [suggestions, setSuggestions] = useState<ARIAFilterSuggestion[]>([])
  const [activeSuggestion, setActiveSuggestion] = useState<number>(-1)
  const [inputSelectionStart, setInputSelectionStart] = useState(-1)
  const [inputSelectionEnd, setInputSelectionEnd] = useState(-1)
  const [styledInputBlockCount, setStyledInputBlockCount] = useState(0)
  const [isInputFocused, setIsInputFocused] = useState(false)
  const id = useId()

  const filterProviders = useMemo<Record<string, FilterProvider>>(() => {
    const record: Record<string, FilterProvider> = {}
    const noProviders: FilterProvider[] = []
    externalProviders.map(provider => {
      record[provider.key] = provider
      if (provider.options?.filterTypes.valueless) {
        noProviders.push(provider)
      }
    })

    // Update legacy is states
    if (record.is && (record.is as IsFilterProvider)?.legacyState && record.state) {
      const stateType = (record.state as StateFilterProvider)?.stateContext
      const stateValues = getFilterValuesByStateType(stateType)
      switch (stateType) {
        case 'mixed': {
          const updateIs = FILTER_VALUES.is.map(v => {
            delete v.icon
            return v
          })
          record.is.filterValues = [...updateIs, ...stateValues]
          break
        }
        case 'issues':
        case 'pr':
        case 'memex': {
          record.is.filterValues = stateValues
          break
        }
        default: {
          record.is.filterValues = [...FILTER_VALUES.is, ...stateValues]
        }
      }
    }

    if (filterConfig.legacyNoProvider) {
      const noProviderValues: FilterSuggestion[] = noProviders
        .sort((a, b) => (a.displayName ?? a.key)?.localeCompare(b.displayName ?? b.key) ?? 0)
        .map(provider => ({
          value: provider.key,
          priority: FILTER_PRIORITY_DISPLAY_THRESHOLD,
          displayName: provider.displayName,
          type: FilterValueType.Value,
          icon: provider.icon,
        }))
      record.no = new NoFilterProvider(noProviderValues)
    }

    parser.current.filterProviders = Object.values(record)
    return record
  }, [externalProviders, filterConfig.legacyNoProvider])

  const {position, updatePosition} = useDelimiterAnchorPosition(
    {
      cursorElementRef: cursorRef,
      floatingElementRef: suggestionsListRef,
      inputElementRef: inputRef,
      side: 'outside-bottom',
      align: 'start',
      alignmentOffset: -16,
      allowOutOfBounds: false,
    },
    [suggestionsListRef.current, styledInputBlockCount],
  )

  // Functions
  const showSuggestions = useCallback(() => {
    if (suggestionsVisible) return
    setSuggestionsVisible(true)
  }, [suggestionsVisible])
  const hideSuggestions = useCallback(() => {
    setSuggestionsVisible(false)
    setActiveSuggestion(-1)
  }, [])
  const focusInput = useCallback(() => inputRef.current?.focus(), [])

  const updateScreenReaderFeedback = useCallback((message: string = '') => {
    if (screenReaderTimeoutRef.current) clearTimeout(screenReaderTimeoutRef.current)
    screenReaderTimeoutRef.current = setTimeout(() => {
      announce(message)
    }, SCREEN_READER_FEEDBACK_TIMEOUT_MS)
  }, [])

  const validateQuery = useCallback(
    async (request: FilterQueryRequest) => {
      let errors = request.errors
      if (!request.isValid && document.activeElement !== inputRef.current) {
        errors = [Strings.unmatchedQuotations]
      }
      onValidation(errors || [])
    },
    [onValidation],
  )

  const cancelUpdateTick = useRef<boolean>(false)

  const debouncedValidate = useDebounce(
    async (query: FilterQueryRequest) => {
      return await parser.current.validateFilterQuery(query)
    },
    100,
    {maxWait: 1000, leading: true, trailing: false},
  )

  const parseAndValidate = useCallback(
    async (caret: number, callback?: (query: FilterQueryRequest) => void, newRaw?: string) => {
      new Promise<FilterQueryRequest>(async (resolve, reject) => {
        const query = parser.current.parse(newRaw ?? rawFilter, filterQuery, caret)
        if (checkFilterQuerySync(query, latestRawFilterRef.current)) {
          setFilterQuery(query)
          resolve(query)
        } else {
          reject(new Error('Query out of sync, Aborted...'))
        }
      })
        // * While we do want to use async/await, in this case it actually results in a broken experience as it blocks
        // * the UI from updating
        // eslint-disable-next-line github/no-then
        .then(async (query: FilterQueryRequest) => {
          return new Promise<FilterQueryRequest>(async (resolve, reject) => {
            const newQuery = await debouncedValidate(query)
            if (newQuery && checkFilterQuerySync(newQuery)) {
              setFilterQuery(newQuery)
              return resolve(newQuery)
            }
            return reject(new Error(newQuery ? 'Out of sync' : 'Empty Query, Aborted...'))
          })
        })
        // eslint-disable-next-line github/no-then
        .then((query: FilterQueryRequest) => {
          callback?.(query)
        })
        // eslint-disable-next-line github/no-then
        .catch(() => {})
    },
    [debouncedValidate, filterQuery, rawFilter],
  )

  const updateSuggestions = useCallback(
    async (filter?: FilterQueryRequest, caret?: number, shouldShowSuggestions = true) => {
      if (variant === 'button') return

      if (cancelUpdateTick.current) {
        cancelUpdateTick.current = false
        return
      }
      // Setting a timeout to show the spinner so it's not flashing
      if (fetchingTimeoutRef.current) clearTimeout(fetchingTimeoutRef.current)
      fetchingTimeoutRef.current = setTimeout(() => setIsFetchingSuggestions(true), 100)
      const query = filter ?? filterQuery
      const cursorIndex = caret ?? inputSelectionStart
      const currentBlock = query.activeBlock

      let aggregatedSuggestions: ARIAFilterSuggestion[] = []
      if (
        (!currentBlock && query.raw === '') ||
        currentBlock?.type === BlockType.Space ||
        (currentBlock?.type === BlockType.Text && currentBlock.raw === '-')
      ) {
        const providers = Object.values(filterProviders)
          .filter(provider => {
            return (
              (provider.options?.filterTypes?.multiKey !== false ||
                (provider.options?.filterTypes?.multiKey === false &&
                  query.blocks.filter(block => isFilterBlock(block) && block.key.value === provider.key).length < 1)) &&
              provider.options?.support.status === ProviderSupportStatus.Supported
            )
          })
          .filter(provider => provider.priority <= FILTER_PRIORITY_DISPLAY_THRESHOLD)
          .sort((a, b) => (a.displayName ?? a.key)?.localeCompare(b.displayName ?? b.key) ?? 0)
          .map(
            (provider): ARIAFilterSuggestion => ({
              type: FilterValueType.Key,
              displayName: provider.displayName,
              ariaLabel: `${provider.displayName}, Filter, ${provider.description}`,
              priority: provider.priority,
              icon: provider.icon,
              value: provider.key,
            }),
          )
        aggregatedSuggestions = providers.slice(0, 8)
      } else if (currentBlock?.type === BlockType.Text) {
        // If a Text value, check provider keys
        const matches: ARIAFilterSuggestion[] = []
        Object.values(filterProviders).map(provider => {
          if (
            (hasMatch(currentBlock.raw, provider.key) ||
              (provider.displayName && hasMatch(currentBlock.raw, provider.displayName)) ||
              (filterConfig.aliasMatching && provider.aliases?.some(a => hasMatch(currentBlock.raw, a))) ||
              (provider.options?.filterTypes.exclusive && hasMatch(currentBlock.raw, `-${provider.key}`))) &&
            (provider.options?.filterTypes.multiKey !== false ||
              query.blocks.filter(block => isFilterBlock(block) && block.key.value === provider.key).length < 1) &&
            provider.options?.support.status === ProviderSupportStatus.Supported
          ) {
            matches.push({
              type: FilterValueType.Key,
              displayName: provider.displayName,
              ariaLabel: `${provider.displayName}, Filter, ${provider.description}`,
              priority: provider.priority,
              icon: provider.icon,
              value: provider.key,
            })
          }
        })
        matches.sort((a, b) => a.priority - b.priority)
        aggregatedSuggestions = matches.slice(0, 8)
      } else if (currentBlock && isFilterBlock(currentBlock)) {
        let filteredSuggestions: ARIAFilterSuggestion[] = []
        const filterKey = currentBlock.key.value.startsWith('-')
          ? currentBlock.key.value.slice(1)
          : currentBlock.key.value
        // Determine where in the block it is
        if (isIndexedFilterBlock(currentBlock)) {
          const [type, chunk] = getFilterBlockChunkByCaret(currentBlock, cursorIndex)
          if (type === 'key') {
            const matches: ARIAFilterSuggestion[] = []
            Object.values(filterProviders).map(provider => {
              if (
                (hasMatch(chunk.value, provider.key) ||
                  (provider.options?.filterTypes.exclusive && hasMatch(chunk.value, `-${provider.key}`))) &&
                (provider.options?.filterTypes.multiKey !== false ||
                  query.blocks.filter(block => isFilterBlock(block) && block.key.value === provider.key).length < 1)
              ) {
                matches.push({
                  type: FilterValueType.Key,
                  displayName: provider.displayName,
                  ariaLabel: `${provider.displayName}, Filter, ${provider.description}`,
                  priority: provider.priority,
                  icon: provider.icon,
                  value: provider.key,
                })
              }
            })
            matches.sort((a, b) => a.priority - b.priority)
            aggregatedSuggestions = matches.slice(0, 8)
          } else if (type === 'value') {
            const provider =
              filterProviders[filterKey] ??
              (filterConfig.aliasMatching
                ? Object.values(filterProviders).find(p => p.aliases?.includes(filterKey))
                : null)
            const providerSuggestions = await provider?.getSuggestions(currentBlock, filterConfig, cursorIndex)
            // Filter out suggestions from this block
            filteredSuggestions =
              providerSuggestions?.filter(
                suggestion =>
                  currentBlock.value.values.findIndex(
                    value => getUnescapedFilterValue(value.value) === suggestion.value && !value.hasCaret,
                  ) < 0,
              ) ?? []
          }
        } else {
          // Not enough info, assume it's value
          const provider =
            filterProviders[filterKey] ??
            (filterConfig.aliasMatching
              ? Object.values(filterProviders).find(p => p.aliases?.includes(filterKey))
              : null)
          const providerSuggestions = await provider?.getSuggestions(currentBlock, filterConfig, cursorIndex)
          // Filter out suggestions from this block
          filteredSuggestions =
            providerSuggestions?.filter(
              suggestion => currentBlock.value.values.findIndex(value => value.value === suggestion.value) < 0,
            ) ?? []
        }
        aggregatedSuggestions = filteredSuggestions.slice(0, 8)
      }

      if (checkFilterQuerySync(query, latestRawFilterRef.current) && query.isValidated) {
        updateScreenReaderFeedback(
          `${aggregatedSuggestions.length} ${aggregatedSuggestions.length === 1 ? 'suggestion' : 'suggestions'}`,
        )
        setSuggestions(aggregatedSuggestions)
        updatePosition()
      }

      if (aggregatedSuggestions.length > 0 && shouldShowSuggestions) showSuggestions()
      else hideSuggestions()
      clearTimeout(fetchingTimeoutRef.current)
      setTimeout(() => {
        setIsFetchingSuggestions(false)
      }, 1000)
    },
    [
      filterConfig,
      filterProviders,
      filterQuery,
      hideSuggestions,
      inputSelectionStart,
      showSuggestions,
      updatePosition,
      updateScreenReaderFeedback,
      variant,
    ],
  )

  const updateInputSelection = useCallback((selectionStart: number, selectionEnd?: number | null) => {
    setInputSelectionStart(selectionStart)
    setInputSelectionEnd(selectionEnd ?? selectionStart)
  }, [])

  const updateFilter = useCallback(
    async (unparsedFilter: string, updateCursorIndex: boolean | number = true, shouldSubmit: boolean = false) => {
      const caret =
        variant !== 'button' && typeof updateCursorIndex === 'number'
          ? updateCursorIndex
          : inputRef.current?.selectionStart ?? -1

      if (updateCursorIndex !== false) {
        if (inputSelectionStart === updateCursorIndex) {
          // Have to forcibly set the input selection to nothing and then reset it. This way, when the new input value is
          // set, the cursor will still have "changed", even if the cursor is in the same position
          updateInputSelection(-1)
          setTimeout(() => updateInputSelection(caret), 1)
        } else {
          updateInputSelection(caret)
        }
      }
      onChange?.(unparsedFilter)
      if (shouldSubmit) {
        latestRawFilterRef.current = unparsedFilter
        parseAndValidate(
          caret,
          query => {
            if (checkFilterQuerySync(query, latestRawFilterRef.current)) onSubmit?.(query)
          },
          unparsedFilter,
        )
      }
    },
    [inputSelectionStart, onChange, onSubmit, parseAndValidate, updateInputSelection, variant],
  )

  const suggestionSelected = useCallback(
    (suggestion: FilterSuggestion) => {
      setActiveSuggestion(-1)
      const currentQuery = filterQuery
      const currentBlock = currentQuery.activeBlock
      let suggestedValue

      if (!currentBlock || currentBlock.type !== BlockType.Filter) {
        suggestedValue = `${currentBlock?.raw.startsWith('-') ? '-' : ''}${getEscapedFilterValue(suggestion.value)}:`
      } else {
        suggestedValue = getEscapedFilterValue(suggestion.value)
        cancelUpdateTick.current = true
        hideSuggestions()
      }

      const caretIndex = inputRef.current?.selectionStart ?? -1

      if (suggestedValue) {
        const [raw, cursorIndex] = parser.current.insertSuggestion(currentQuery, suggestedValue, caretIndex)
        updateFilter(raw, cursorIndex)
      } else {
        updateFilter(currentQuery.raw, false)
      }

      focusInput()
    },
    [filterQuery, focusInput, hideSuggestions, updateFilter],
  )

  const forceReparse = useCallback(
    async (cursorLocation?: number, simpleReparse = false) => {
      const caret = cursorLocation ? cursorLocation : inputRef.current?.selectionStart ?? -1
      parseAndValidate(caret, query => {
        validateQuery(query)
        if (!simpleReparse && checkFilterQuerySync(query, latestRawFilterRef.current))
          updateSuggestions(query, caret, query.raw === '')
      })
    },
    [parseAndValidate, updateSuggestions, validateQuery],
  )

  // Events
  const onSubmitHandler = useCallback(() => {
    onSubmit?.(filterQuery)
  }, [filterQuery, onSubmit])

  const inputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        hideSuggestions()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (event.key === 'ArrowDown') {
        if (activeSuggestion + 1 >= suggestions.length) setActiveSuggestion(-1)
        else setActiveSuggestion(activeSuggestion + 1)
        event.preventDefault()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (event.key === 'ArrowUp') {
        if (activeSuggestion === -1) setActiveSuggestion(suggestions.length - 1)
        else setActiveSuggestion(activeSuggestion - 1)
        event.preventDefault()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (event.key === 'Enter') {
        if (suggestionsVisible && activeSuggestion !== null) {
          const suggestion = suggestions[activeSuggestion]
          if (suggestion) {
            event.preventDefault()
            suggestionSelected(suggestion)
          } else {
            onSubmitHandler()
          }
        } else {
          onSubmitHandler()
        }
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (event.key === 'Escape') {
        hideSuggestions()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (event.key === 'Home' || event.key === 'End') {
        setActiveSuggestion(-1)
      }
    },
    [activeSuggestion, hideSuggestions, onSubmitHandler, suggestionSelected, suggestions, suggestionsVisible],
  )

  const inputOnFocus = useCallback(() => {
    setIsInputFocused(true)
    if (isInteractingWithSuggestions) {
      setIsInteractingWithSuggestions(false)
    } else {
      forceReparse(-1)
    }
  }, [isInteractingWithSuggestions, forceReparse])

  const inputOnBlur = useCallback(() => {
    if (isInteractingWithSuggestions) {
      return
    }
    setIsInputFocused(false)
    forceReparse(-1, true)
    hideSuggestions()
  }, [isInteractingWithSuggestions, forceReparse, hideSuggestions])

  const inputOnChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    e => {
      e.preventDefault()
      updateFilter(e.currentTarget.value, e.currentTarget.selectionStart ?? -1)
    },
    [updateFilter],
  )

  const updateStyledInputBlockCount = useCallback((count: number) => {
    setStyledInputBlockCount(count)
  }, [])

  const suspendFocus = useCallback(() => {
    setIsInteractingWithSuggestions(true)
  }, [])

  const clearFilter = useCallback(() => {
    onChange('')
    focusInput()
  }, [focusInput, onChange])

  useLayoutEffect(() => {
    const updateFromExternal = async () => {
      latestRawFilterRef.current = rawFilter

      parseAndValidate(inputSelectionStart, query => {
        validateQuery(query)
        onParse?.(query)
        if (checkFilterQuerySync(query, latestRawFilterRef.current)) {
          updateSuggestions(query, inputSelectionStart, isInputFocused)
        }
      })
    }

    if (rawFilter !== latestRawFilterRef.current) {
      updateFromExternal()
    }
  }, [
    rawFilter,
    inputSelectionStart,
    isInputFocused,
    updateSuggestions,
    validateQuery,
    variant,
    onParse,
    filterQuery.raw,
    filterQuery,
    parseAndValidate,
  ])

  const contextValue: FilterContextType = useMemo(
    () => ({
      config: filterConfig,
      id,
      onSubmit: onSubmitHandler,
    }),
    [filterConfig, id, onSubmitHandler],
  )

  const suggestionsContextValue = useMemo(
    () => ({
      activeSuggestion,
      hideSuggestions,
      isFetchingSuggestions,
      position,
      showSuggestions,
      suggestions,
      suggestionsListRef,
      suggestionSelected,
      suggestionsVisible,
      updateSuggestions,
    }),
    [
      activeSuggestion,
      hideSuggestions,
      isFetchingSuggestions,
      position,
      showSuggestions,
      suggestions,
      suggestionSelected,
      suggestionsVisible,
      updateSuggestions,
    ],
  )

  const inputContextValue = useMemo(
    () => ({
      cursorRef,
      inputFocused: isInputFocused,
      inputKeyDown,
      inputOnBlur,
      inputOnChange,
      inputOnFocus,
      inputRef,
      inputSelectionEnd,
      inputSelectionStart,
      shouldShowSpinner: isFetchingResults,
      suspendFocus,
      updateInputSelection,
      updateStyledInputBlockCount,
    }),
    [
      inputKeyDown,
      inputOnBlur,
      inputOnChange,
      inputOnFocus,
      inputSelectionEnd,
      inputSelectionStart,
      isFetchingResults,
      isInputFocused,
      suspendFocus,
      updateInputSelection,
      updateStyledInputBlockCount,
    ],
  )

  const filterQueryContextValue = useMemo(
    () => ({
      clearFilter,
      filterQuery,
      filterProviders,
      forceReparse,
      rawFilter,
      updateFilter,
    }),
    [clearFilter, filterProviders, filterQuery, forceReparse, rawFilter, updateFilter],
  )

  return (
    <FilterContext.Provider value={contextValue}>
      <SuggestionsContext.Provider value={suggestionsContextValue}>
        <InputContext.Provider value={inputContextValue}>
          <FilterQueryContext.Provider value={filterQueryContextValue}>{children}</FilterQueryContext.Provider>
        </InputContext.Provider>
      </SuggestionsContext.Provider>
    </FilterContext.Provider>
  )
}

try{ FilterContext.displayName ||= 'FilterContext' } catch {}
try{ FilterContextProvider.displayName ||= 'FilterContextProvider' } catch {}